import { Splash } from "@mux/ui/logo"
import { ServerConnection, serverName, useServer } from "@/context/server"
import { useFleet } from "@/context/fleet"
import { fleetStartupReady } from "@/fleet/startup"
import { parseMachineFromPath } from "@/utils/machine-route"
import { useLocation } from "@solidjs/router"
import { createEffect, createMemo, type ParentProps, Show } from "solid-js"

export type MachineRouteConnection = { key: ServerConnection.Key; name: string }

function resolveMachineRouteConnection(
  name: string,
  views: ReadonlyArray<{ key: ServerConnection.Key; name: string }>,
  connections: ReadonlyArray<ServerConnection.Any>,
): MachineRouteConnection | undefined {
  const view = views.find((machine) => machine.name === name)
  if (view) return { key: view.key, name }
  const connection = connections.find((item) => serverName(item) === name)
  if (connection) return { key: ServerConnection.key(connection), name }
}

export function syncMachineRoute(input: {
  name: string
  connection?: MachineRouteConnection
  activeKey: ServerConnection.Key | undefined
  clearActive: () => void
  setActive: (key: ServerConnection.Key, options: { machineName: string }) => void
  rememberMachine: (name: string) => void
}) {
  if (!input.connection) {
    input.clearActive()
    return
  }
  if (input.activeKey !== input.connection.key) {
    input.setActive(input.connection.key, { machineName: input.name })
    return
  }
  input.rememberMachine(input.name)
}

/**
 * Selects the route's machine, which pages without a machine in their URL
 * (settings, machines) act on, and holds the dashboard back until the fleet
 * knows enough to show the route. Once shown, the dashboard stays mounted:
 * moving to another machine only changes the selection, and every surface
 * reads its own machine's scope. A route naming no registered machine says so
 * where the route's content shows.
 */
export function MachineRouteController(props: ParentProps) {
  const location = useLocation()
  const server = useServer()
  const fleet = useFleet()
  const routeMachine = createMemo(() => parseMachineFromPath(location.pathname))
  const routeConnection = createMemo(() => {
    const name = routeMachine()
    if (!name) return undefined
    return resolveMachineRouteConnection(name, fleet.views(), server.list)
  })

  createEffect(() => {
    if (!fleetStartupReady(fleet.machines.loading, fleet.views().length)) return
    const name = routeMachine()
    if (!name) return
    syncMachineRoute({
      name,
      connection: routeConnection(),
      activeKey: server.key,
      clearActive: server.clearActive,
      setActive: server.setActive,
      rememberMachine: server.rememberMachine,
    })
  })

  // Latched: once the dashboard shows, no later route takes it down.
  const started = createMemo(
    (shown) => shown || !routeMachine() || fleetStartupReady(fleet.machines.loading, routeConnection() ? 1 : 0),
    false,
  )

  return (
    <Show
      when={started()}
      fallback={
        <div
          class="h-dvh w-screen bg-background-base flex flex-col items-center justify-center gap-3"
          role="status"
          aria-live="polite"
        >
          <Splash class="w-10 h-12 animate-pulse" />
          <p class="text-13-regular text-text-weak">Loading chats…</p>
        </div>
      }
    >
      {props.children}
    </Show>
  )
}
